// Per-user sidebar menu layout state.
//
// Holds the RAW stored layout (null = not customized / not yet loaded). The
// sidebar and the editor reconcile it against the live registry at point of
// use. The root reducer (store/index.ts) resets this slice on logout, so each
// user fetches their own layout fresh.

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { StoredLayout } from '../types/navLayout';
import { navigationLayoutService } from '../services/navigationLayoutService';

interface MenuLayoutState {
  layout: StoredLayout | null;
  status: 'idle' | 'loading' | 'loaded' | 'error';
  saving: boolean;
  error: string | null;
}

const initialState: MenuLayoutState = {
  layout: null,
  status: 'idle',
  saving: false,
  error: null,
};

export const fetchMenuLayout = createAsyncThunk('menuLayout/fetch', async () => {
  return await navigationLayoutService.get();
});

export const saveMenuLayout = createAsyncThunk('menuLayout/save', async (layout: StoredLayout) => {
  return await navigationLayoutService.save(layout);
});

export const resetMenuLayout = createAsyncThunk('menuLayout/reset', async () => {
  await navigationLayoutService.reset();
  return null;
});

const menuLayoutSlice = createSlice({
  name: 'menuLayout',
  initialState,
  reducers: {
    // Optimistic local update (used by the editor before/independently of save).
    setLayoutLocal(state, action: PayloadAction<StoredLayout | null>) {
      state.layout = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMenuLayout.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchMenuLayout.fulfilled, (state, action) => {
        state.status = 'loaded';
        state.layout = action.payload;
      })
      .addCase(fetchMenuLayout.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.error.message ?? 'Failed to load menu layout';
      })
      .addCase(saveMenuLayout.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveMenuLayout.fulfilled, (state, action) => {
        state.saving = false;
        state.status = 'loaded';
        state.layout = action.payload;
      })
      .addCase(saveMenuLayout.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message ?? 'Failed to save menu layout';
      })
      .addCase(resetMenuLayout.fulfilled, (state) => {
        state.layout = null;
        state.status = 'loaded';
      });
  },
});

export const { setLayoutLocal } = menuLayoutSlice.actions;
export default menuLayoutSlice.reducer;
